import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface GoogleContinueProps {
    /** The Google OAuth client id from /signup/config; nothing renders without one. */
    clientId: string | null | undefined;
    /** The ID token Google hands back, for the API to verify. */
    onCredential: (credential: string) => void;
    onError?: () => void;
    /** What Google's own button says. */
    text?: 'continue_with' | 'signin_with' | 'signup_with';
    /** Google's wording around the button: signing up, signing in, or using an app. */
    context?: 'signup' | 'signin' | 'use';
    locale?: string;
    /** "or" and a rule under the button, before the email form. */
    divider?: boolean;
    className?: string;
    testId?: string;
}

/**
 * Google's own sign-in button, the same on every auth page (sign up, sign in,
 * accept an invitation): Google requires its button as it draws it, so the
 * page around it only adds the spacing and the "or" divider.
 */
export function GoogleContinue({ clientId, onCredential, onError, text = 'continue_with', context, locale, divider = true, className, testId = 'google-continue' }: GoogleContinueProps) {
    const { t, i18n } = useTranslation();
    if (!clientId) return null;
    return (
        <div className={cn('animate-form-item animate-delay-2', className)} data-testid={testId}>
            <GoogleOAuthProvider clientId={clientId}>
                <div className="flex justify-center [&_iframe]:!mx-auto">
                    <GoogleLogin
                        onSuccess={(response) => (response.credential ? onCredential(response.credential) : onError?.())}
                        onError={() => onError?.()}
                        text={text}
                        {...(context ? { context } : {})}
                        shape="rectangular"
                        theme="outline"
                        size="large"
                        width="360"
                        // Google picks a language from the visitor's region otherwise; the page's wins.
                        locale={locale || i18n?.language || 'en'}
                    />
                </div>
            </GoogleOAuthProvider>
            {divider && (
                <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {t('AUTH.OR', 'or')}
                    <span className="h-px flex-1 bg-border" />
                </div>
            )}
        </div>
    );
}
